import { activeLocale, translate, type Locale } from '@/i18n/locale';

const API_BASE = '/api/v1';

/** Shape Laravel uses for both plain failures and 422 validation errors. */
interface ErrorBody {
  message?: string;
  errors?: Record<string, string[]>;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Field errors flattened to one message each, which is all a form can show
   * next to an input. Empty for anything that is not a 422.
   */
  get fieldErrors(): Record<string, string> {
    const errors = (this.body as ErrorBody | null)?.errors;

    if (!errors) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(errors).flatMap(([field, messages]) =>
        messages[0] ? [[field, messages[0]]] : [],
      ),
    );
  }
}

/**
 * Thin fetch wrapper.
 *
 * Auth relies on HttpOnly cookies, so credentials must always be included;
 * no token is ever stored in JS-reachable storage.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      // Whatever is on screen, which for a signed-in user is their account's
      // own setting rather than the browser's. That is what lets the server
      // answer in the language of the interface without reading the database.
      'Accept-Language': activeLocale(),
      'X-Requested-With': 'XMLHttpRequest',
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  const contentType = response.headers.get('content-type') ?? '';
  const payload: unknown = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    // Laravel's own message is far more useful in a form than a generic one,
    // and it arrives in the language `Accept-Language` asked for above, so it
    // is safe to display as-is. Only the fallback is translated here.
    const message =
      (payload as ErrorBody | null)?.message ??
      translate(activeLocale(), 'errors.request_failed', { path });

    throw new ApiError(message, response.status, payload);
  }

  return payload as T;
}

export interface HealthCheck {
  status: 'ok' | 'fail';
  latency_ms?: number;
  error?: string;
}

export interface HealthResponse {
  status: 'ok' | 'degraded';
  app: string;
  env: string;
  version: string;
  checks: Record<string, HealthCheck>;
  timestamp: string;
}

export function fetchHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('/health');
}

export interface User {
  id: number;
  name: string;
  email: string;
  timezone: string;
  /** The account's own choice, which outranks the browser's preference. */
  language: Locale;
  notification_time: string;
  notification_channel: 'push' | 'email' | 'both' | 'off';
  biometric_consent: boolean;
  /** The server's view of the user's calendar day; the client must not guess. */
  today: string;
  streak: { current: number; longest: number; total_photos: number };
  created_at: string | null;
}

export interface LoginPayload {
  email: string;
  password: string;
  /** Lets a traveller's calendar day follow them without a settings trip. */
  timezone?: string;
}

export interface RegisterPayload {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  timezone: string;
  /** Seeded from what the sign-up screen was already being read in. */
  language: Locale;
  biometric_consent: boolean;
}

export interface ResetPasswordPayload {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
}

const json = (body: unknown): RequestInit => ({
  method: 'POST',
  body: JSON.stringify(body),
});

export function fetchMe(): Promise<{ user: User }> {
  return apiFetch<{ user: User }>('/me');
}

export function login(payload: LoginPayload): Promise<{ user: User }> {
  return apiFetch<{ user: User }>('/login', json(payload));
}

/**
 * Answers with a message, not a user: signing up starts no session, because
 * the address it was given has not been confirmed yet.
 */
export function register(payload: RegisterPayload): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/register', json(payload));
}

export function resendVerification(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(
    '/email/verification-notification',
    json({ email }),
  );
}

/**
 * Whether a failed sign-in was the one failure worth offering a button for.
 *
 * The API answers an unconfirmed address with a code rather than only prose,
 * precisely so this can be told apart from a wrong password without matching
 * on a sentence that changes with the locale.
 */
export function isUnverifiedEmail(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.status === 403 &&
    (error.body as { code?: string } | null)?.code === 'email_unverified'
  );
}

export function logout(): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/logout', { method: 'POST' });
}

export function requestPasswordReset(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/forgot-password', json({ email }));
}

export function resetPassword(
  payload: ResetPasswordPayload,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/reset-password', json(payload));
}

export type PhotoStatus = 'pending' | 'processing' | 'ready' | 'failed';

export interface Photo {
  id: number;
  date: string;
  status: PhotoStatus;
  width: number | null;
  height: number | null;
  from_gallery: boolean;
  /** Short-lived signed URLs; the bucket itself is private. */
  url: string | null;
  thumb_url: string | null;
  captured_at: string | null;
}

export interface TodayResponse {
  date: string;
  photo: Photo | null;
  /** Most recent earlier photo, drawn over the preview as the ghost outline. */
  previous: Photo | null;
}

export interface UploadTicket {
  photo: Photo;
  upload: {
    url: string;
    headers: Record<string, string>;
    expires_at: string;
  };
}

export interface UploadUrlPayload {
  mime: string;
  bytes: number;
  width: number;
  height: number;
  checksum: string;
  from_gallery?: boolean;
  /** Absent whenever no face was found, or the model was never loaded. */
  align_transform?: { matrix: number[]; width: number; height: number };
  landmarks?: { left: { x: number; y: number }; right: { x: number; y: number } };
  align_score?: number;
}

export function fetchToday(): Promise<TodayResponse> {
  return apiFetch<TodayResponse>('/photos/today');
}

export function requestUploadUrl(payload: UploadUrlPayload): Promise<UploadTicket> {
  return apiFetch<UploadTicket>('/photos/upload-url', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function completeUpload(photoId: number): Promise<{ photo: Photo }> {
  return apiFetch<{ photo: Photo }>(`/photos/${photoId}/complete`, { method: 'POST' });
}

export function deletePhoto(photoId: number): Promise<unknown> {
  return apiFetch(`/photos/${photoId}`, { method: 'DELETE' });
}

export interface FeedPage {
  photos: Photo[];
  /** Opaque; echo it back verbatim to get the next page. */
  next_cursor: string | null;
}

export function fetchFeed(cursor: string | null): Promise<FeedPage> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';

  return apiFetch<FeedPage>(`/photos${query}`);
}

export interface CalendarDay {
  date: string;
  photo: Photo | null;
  /** A day still to come is not a missed day. */
  is_future: boolean;
}

export interface CalendarMonth {
  year: number;
  month: number;
  days: CalendarDay[];
  filled: number;
}

export function fetchCalendar(year: number, month: number): Promise<CalendarMonth> {
  return apiFetch<CalendarMonth>(`/calendar/${year}/${month}`);
}

export type TimelapseStatus = 'queued' | 'processing' | 'ready' | 'failed' | 'expired';

export interface Timelapse {
  id: number;
  range: string;
  status: TimelapseStatus;
  fps: number;
  frame_count: number | null;
  bytes: number | null;
  error: string | null;
  url: string | null;
  expires_at: string | null;
  created_at: string | null;
}

export interface SettingsPayload {
  notification_time?: string;
  notification_channel?: User['notification_channel'];
  timezone?: string;
  language?: Locale;
}

export function updateSettings(payload: SettingsPayload): Promise<{ user: User }> {
  return apiFetch<{ user: User }>('/settings', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function requestTimelapse(range: string): Promise<{ timelapse: Timelapse }> {
  return apiFetch<{ timelapse: Timelapse }>('/timelapses', json({ range }));
}

export function fetchTimelapse(id: number): Promise<{ timelapse: Timelapse }> {
  return apiFetch<{ timelapse: Timelapse }>(`/timelapses/${id}`);
}
